import styled from '@emotion/styled';
import { AppUI, ExerciseUI, ForumUI } from '@seedlang/state';
import { isPresent, isBlank } from '@seedlang/utils';
import { noop } from 'lodash';
import autobind from 'autobind-decorator';
import CommentIndex from 'components/comment/comment_index';
import Description from 'components/exercise/description';
import ExerciseHighlight from 'components/exercise/exercise_highlight';
import ExerciseDrag from 'components/exercise/exercise_drag';
import ExerciseWordEnter from 'components/exercise/exercise_word_enter';
import ExerciseWordOptions from 'components/exercise/exercise_word_options';
import ExerciseMatchMedia from 'components/exercise/exercise_match_media';
import ExerciseMatchText from 'components/exercise/exercise_match_text';
import ExerciseMultipleChoiceMedia from 'components/exercise/exercise_multiple_choice_media';
import ExerciseReorder from 'components/exercise/exercise_reorder';
import ExerciseSentenceEnter from 'components/exercise/exercise_sentence_enter';
import ExerciseTags from 'components/exercise/exercise_tags';
import ExpandableDescription from 'components/exercise/expandable_description';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import Spinner from 'components/spinner';
import Text from 'components/text';
import ExerciseButtons from 'components/worksheet/exercise_buttons';
import ExerciseNavigation from 'components/worksheet/exercise_navigation';
import NoAccessButton from 'components/worksheet/no_access_button';
import XpAnimation from 'components/xp_animation';
import YoutubeVideo from 'components/youtube_video';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router';
import TextDisplay from 'components/text_display';
import Stack, { gaps } from 'components/layout/stack';
import { Theme } from '@seedlang/constants';
import ReactTooltip from 'react-tooltip';
import cx from 'classnames';

const COMPONENTS = {
  matchText: ExerciseMatchText,
  reorder: ExerciseReorder,
  wordEnter: ExerciseWordEnter,
  drag: ExerciseDrag,
  wordOptions: ExerciseWordOptions,
  matchMedia: ExerciseMatchMedia,
  sentenceEnter: ExerciseSentenceEnter,
  highlight: ExerciseHighlight,
  multipleChoice: ExerciseMultipleChoiceMedia,
  simpleTextPrompt: () => null,
};

const slugsWithEnglishPrompts = ['reorder'];

const Wrapper = styled.div`
  .fa-angle-left {
    font-weight: bold;
    margin-right: 5px;
    font-size: 20px;
    line-height: 25px;
  }
`;

const Content = styled.div`
  background: white;
  padding: 20px;
  border-radius: ${props => props.borderRadius}; 
`;

const Exercise = styled.div`

`;

const AdminLink = styled.div`
  margin-top: 5px;
  font-size: 11px;
  text-decoration: underline;
  text-align: center;
  padding: 5px;
`;

const InnerWrapper = styled.div`
  display: grid;
  grid-template-columns: 18% 80%;
  gap: 20px;
  @media (max-width: 899px) {
    display: flex;
    flex-direction: column;
  }
  height: ${props => props.hasAccess ? null : '250px'};
  mask-image: ${props => props.hasAccess ? null : 'linear-gradient(to bottom,black,70%,transparent)'};
  pointer-events: ${props => props.hasAccess ? 'auto' : 'none'};
  user-select: ${props => props.hasAccess ? 'auto' : 'none'};
  overflow: ${props => props.hasAccess ? 'visible' : 'hidden'};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: 0 0 10px 0;
  align-items: center;
  gap: ${gaps.sm};
`;

const EyeToggle = styled.button`
  padding: 0;
  font-size: 18px;
  color: ${Theme.darkGray};
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
  display: flex;

  &:hover {
    color: black;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  max-width: 540px;
  aspect-ratio: 16 / 9;
  margin: ${gaps.md} auto;
`;

@observer
class ExerciseShow extends React.Component {
  @observable showUserSettings;
  @observable comment = '';
  @observable showRatingComment = false;
  @observable ratingCommentSubmitted = false;
  @observable showSpinner = true;

  constructor(props) {
    super(props);
    ExerciseUI.set('exerciseId', this.props.params.exerciseId);
    ExerciseUI.enrichExercise(this.props.params.exerciseId, this.afterEnrichExercise);
  }

  componentDidMount() {
    this.initializeExercise();
    if (!AppUI.membershipTypeStore.hasIndexData) {
      AppUI.membershipTypeStore.getIndex();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.params.exerciseId !== this.props.params.exerciseId) {
      this.initializeExercise();
    }
  }

  @autobind initializeExercise() {
    ExerciseUI.set('exerciseId', this.props.params.exerciseId);
    ExerciseUI.enrichExercise(this.props.params.exerciseId, this.afterEnrichExercise);
    this.showRatingComment = false;
    this.ratingCommentSubmitted = false;
    this.comment = '';
  }

  @autobind afterEnrichExercise() {
    AppUI.scrollToPageTop('auto');
    this.showSpinner = false;
    if (isBlank(ExerciseUI.worksheetUserExerciseStore.indexData) || !ExerciseUI.worksheetUserExerciseStore.indexData?.find(item => item.exerciseId === this.props.params.exerciseId)) {
      ExerciseUI.worksheetUserExerciseStore.getIndex({ids: {worksheetId: ExerciseUI.worksheet?.id || this.props.params.worksheetId}});
    }
  }

  componentWillUnmount() {
    ExerciseUI.set('exerciseId', null);
  }

  @autobind topIsInView(el) {
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.top <= (window.innerHeight || document.documentElement.clientHeight)
    );
  }

  @autobind setComment(comment) {
    this.comment = comment;
  }

  @autobind afterSubmitComment() {
    this.commentTimestamp = +new Date();
  }

  @autobind goBackToTop() {
    AppUI.scrollToPageTop();
  }

  @autobind onSubmitRating(rating) {
    this.showRatingComment = true;
    ExerciseUI.onClickRating(rating);
  }

  @computed get exercisesAreBlocked() {
    return !ExerciseUI.hasCurrentWorksheetAccess || (AppUI.exercisesBlockedUntilMigrated && !ExerciseUI.worksheet.accessWithoutMembership) || !this.hasSiteExerciseAccess();
  }

  @autobind onSubmitRatingComment() {
    ExerciseUI.onSubmitRatingComment(this.comment);
    this.ratingCommentSubmitted = true;
    this.comment = '';
  }

  @computed get contentBorderRadius() {
    if (ExerciseUI.layout.isMobile) {
      return '0';
    }
    if (ExerciseUI.exercise?.forumThread && ExerciseUI.site.showWorksheetComments) {
      if (this.props.tabCount <= 1) {
        return '10px 10px 0 0';
      }
      return '0px 10px 0 0';
    } if (this.props.tabCount <= 1) {
      return '10px';
    }
    return '0 10px 10px 10px';
  }

  @computed get commentsBorderRadius() {
    if (ExerciseUI.layout.isMobile) {
      return '0';
    }
    return '0 0 10px 10px';
  }

  @autobind goToNextExercise() {
    ExerciseUI.goToNextExercise(() => AppUI.scrollToPageTop('auto'), ExerciseUI.hasWorksheet ? `${ExerciseUI.worksheetNamespace}.exercises.show` : 'exercises.show');
  }

  @autobind toggleEnglishPrompts() {
    ExerciseUI.authUserStore.update({data: { hideEnglishPrompts: !ExerciseUI.user.hideEnglishPrompts }}, noop);
    ExerciseUI.user.set('hideEnglishPrompts', !ExerciseUI.user.hideEnglishPrompts);
  }

  hasSiteExerciseAccess() {
    if (ExerciseUI.user.anyAdmin || ExerciseUI.worksheet.accessWithoutMembership) return true;

    // Check for Patreon access on default site (Easy German)
    if (AppUI.siteIsDefault && (ExerciseUI.user.permanentPatreonMember || ExerciseUI.user.activePatreonMember)) {
      return true;
    }

    const userMembershipTypeId = ExerciseUI.user.currentGroupMembership?.membershipTypeId;
    if (!userMembershipTypeId) return false;
    const userMembershipType = AppUI.membershipTypeStore.indexData.find(mt => mt.id === userMembershipTypeId);
    return userMembershipType?.canAccessExercises ?? true;
  }

  render() {
    return (
      <Wrapper>
        <Content
          borderRadius={this.contentBorderRadius}
        >
          <InnerWrapper
            hasAccess={!this.exercisesAreBlocked}
          >
            <ExerciseNavigation
              padding='0'
              margin='0 0 20px 0'
              exercises={ExerciseUI.hasWorksheet && ExerciseUI.worksheetStore.showData.exercises.filter(item => item.exerciseType.slug !== null)}
              currentExerciseId={ExerciseUI.exercise?.id}
              worksheetId={ExerciseUI.worksheet?.id}
              useWorksheetRoute={ExerciseUI.hasWorksheet}
              afterRouteToExercise={this.scrollToTop}
              hasAccess={!this.exercisesAreBlocked}
            />
          {
            ExerciseUI.hasExercise &&
              <Exercise className='exercise-wrapper'>
                <TitleWrapper>
                  <Text heading={3}>{ExerciseUI.exercise.name}</Text>
                  <Stack inline gap='sm' alignItems='center'>
                    <ExerciseTags exercise={ExerciseUI.exercise} />
                    {
                      slugsWithEnglishPrompts.includes(ExerciseUI.exercise?.exerciseType?.slug) &&
                        <EyeToggle
                          onClick={this.toggleEnglishPrompts}
                          data-tip
                          data-for='english-prompts'
                        >
                          <i
                            className={cx('fa', {
                              'fa-eye-slash': ExerciseUI.user.hideEnglishPrompts,
                              'fa-eye': !ExerciseUI.user.hideEnglishPrompts,
                            })}
                          />
                          <ReactTooltip id='english-prompts' place='top'>
                            {ExerciseUI.user.hideEnglishPrompts ? 'Show English Prompts' : 'Hide English Prompts'}
                          </ReactTooltip>
                        </EyeToggle>
                    }
                  </Stack>
                </TitleWrapper>
                {
                  ExerciseUI.exercise.description && ExerciseUI.exercise.autoExpandDescription &&
                    <Description>
                      <TextDisplay
                        htmlText={ExerciseUI.exercise.descriptionFormatted}
                      />
                    </Description>
                }
                {
                  ExerciseUI.exercise.defaultDescription &&
                    <ExpandableDescription
                      exerciseId={ExerciseUI.exercise?.id}
                      defaultDescription={ExerciseUI.exercise.defaultDescriptionFormatted}
                      expandedDescription={ExerciseUI.exercise.expandedDescriptionFormatted}
                      blockExpansion={this.exercisesAreBlocked}
                    />
                }
                {
                  ExerciseUI.exercise.formattedDescriptionVideo &&
                    <VideoWrapper>
                      <YoutubeVideo
                        url={ExerciseUI.exercise.formattedDescriptionVideo}
                        show
                        allowFloating
                      />
                    </VideoWrapper>
                }
                {
                  !ExerciseUI.exercise?.enriched && ExerciseUI.exercise?.exerciseEntriesCount > 0 &&
                    <Spinner
                      background={AppUI.site.accentColor || '#868686'}
                    />
                }
                {
                  ExerciseUI.exercise?.exerciseType?.slug &&
                    React.createElement(COMPONENTS[ExerciseUI.exercise.exerciseType.slug],
                      {
                        exerciseId: ExerciseUI.exercise.id,
                        isMobile: ExerciseUI.layout.isMobile,
                        hideEnglishPrompts: ExerciseUI.user.hideEnglishPrompts,
                        inDeck: false,
                      },
                    )
                }
                {
                  !this.exercisesAreBlocked && isPresent(ExerciseUI.exercise) && isPresent(ExerciseUI.exercise.exerciseType) && isPresent(ExerciseUI.exercise.exerciseType.slug) &&
                    <ExerciseButtons
                      exercisesAreBlocked={this.exercisesAreBlocked}
                      goToNextExercise={this.goToNextExercise}
                      showRatingComment={this.showRatingComment}
                      goBackToTop={this.goBackToTop}
                      onSubmitRating={this.onSubmitRating}
                      onSubmitRatingComment={this.onSubmitRatingComment}
                      setComment={this.setComment}
                      comment={this.comment}
                      ratingCommentSubmitted={this.ratingCommentSubmitted}
                    />
                }
                {
                  AppUI.siteIsDefault &&
                    <XpAnimation
                      namespace='user-exercise-show'
                    />
                }
              </Exercise>
          }
          </InnerWrapper>
          {
            ExerciseUI.hasWorksheet && this.exercisesAreBlocked &&
              <NoAccessButton
                migrate={AppUI.vocabBlockedUntilMigrated}
                register={ExerciseUI.worksheet.accessWithoutMembership}
              />
          }
        </Content>
        {
          ExerciseUI.exercise?.forumThread && ExerciseUI.site.showWorksheetComments &&
            <CommentIndex
              hideUserProfile
              showCommentCreate={!this.exercisesAreBlocked}
              commentTimestamp={this.commentTimestamp}
              groupId={ExerciseUI.exercise.worksheet.groupId}
              inModal={false}
              post={ExerciseUI.exercise.forumThread}
              showCommentCount
              user={ForumUI.user}
              commentable={ExerciseUI.exercise.forumThread}
              commentableType='ForumThread'
              forumThreadId={ExerciseUI.exercise.forumThread?.id}
              margin='0'
              languageId={ExerciseUI.siteIsDefault ? 'DE' : ExerciseUI.site.languageId}
              borderRadius={this.commentsBorderRadius}
              padding='40px 20px 20px 20px'
              placeholder='Leave a comment about the exercise'
              indentation={ExerciseUI.layout.isMobile ? 20 : 90}
            />
        }
        {
          !AppUI.siteIsDefault && ExerciseUI.user.anyAdmin && ExerciseUI.exercise && ExerciseUI.exercise.worksheet && ExerciseUI.exercise.worksheet.groupId &&
            <AdminLink>
              <Link
                to={{name: 'creator.exercises.edit', params: {groupId: ExerciseUI.exercise.worksheet.groupId, worksheetId: ExerciseUI.exercise.worksheet.id, exerciseId: ExerciseUI.exercise.id}}}
              >
                Admin Link
              </Link>
            </AdminLink>
        }
      </Wrapper>
    );
  }
}

export default ErrorBoundary(ExerciseShow);
